/**
 * feat-241 — a passport card's artwork, with the THREE-TIER fallback that
 * FRONTEND-SLICE §4 RULING 1 makes part of AC18.
 *
 *   completed_image_s3_key  →  image_s3_key  →  monogram tile
 *
 * ── WHY THIS IS NOT AN EDGE CASE ────────────────────────────────────────────
 * AC18 as written says "completed tracks use `completed_image_s3_key`". Read
 * literally against a tenant with NO ART that is unsatisfiable — and no art is
 * the DEFAULT case today: the program file records that the mascot tier is
 * structurally single-tenant and that no track shows art on the shipped Flutter
 * screen. The same S3 keys back this surface, so on the day Grove's manager first
 * opens this page the art may be entirely absent. A passport that only looks
 * right with art will look wrong for the tenant it was built for.
 *
 * AC18 is therefore satisfied by rendering the completed art WHEN THE KEY IS
 * PRESENT and the designed fallback when it is not — and both arms are tested. A
 * test exercising only the art-present arm passes against data no tenant
 * currently has.
 *
 * A completed track falling back to its in-progress art is CORRECT: the chrome
 * (the chip, the group heading) carries the completed state, not which file
 * resolved. D10's completed art is a reward when it exists, never the state's
 * only carrier.
 *
 * `onError` is wired AS WELL AS the null check, so a present-but-broken key lands
 * in the same fallback rather than the browser's broken-image glyph.
 *
 * ── "LOCKED ONES DIMMED" ────────────────────────────────────────────────────
 * The dim is `grayscale opacity-40` ON THE ARTWORK ONLY. Dimming the whole card
 * is what "dimmed" gets built as by default and it takes the INK down with it —
 * `inkMuted` at 40% composites to well under 2:1. Ink stays at full opacity;
 * only the picture recedes.
 */
import React, { useState } from 'react';
import { trackImagePublicUrl } from '../../../services/quizContentAdminService';
import { trackMonogram } from '../../tenants/curriculum/tracks/trackArt';
import type { CellStatus } from '../../../constants/curriculumCoverage';
import type { CoverageTrack } from '../../../services/curriculumCoverageService';

/** The monogram tile's wash — the state's own, so it still reads as its state. */
const MONOGRAM_WASH: Record<CellStatus, string> = {
  completed: 'bg-success-light text-success-on',
  in_progress: 'bg-gold-light text-gold-on',
  available: 'bg-gray-100 text-inkMuted',
  locked: 'bg-cream-dark text-inkMuted',
};

export interface PassportTrackArtProps {
  track: CoverageTrack;
  status: CellStatus;
}

export const PassportTrackArt: React.FC<PassportTrackArtProps> = ({ track, status }) => {
  // Tier 1 for a completed track, tier 2 otherwise (and as tier 2 for a completed
  // track whose completed art is missing).
  const preferred = status === 'completed' ? track.completed_image_s3_key : null;
  const chain = [preferred, track.image_s3_key]
    .map((k) => trackImagePublicUrl(k))
    .filter((u): u is string => Boolean(u));

  // How many of the chain's URLs have failed to load. Advancing this is what
  // turns a present-but-BROKEN key into the next tier instead of a broken-image
  // glyph — the null check alone cannot see a 404.
  const [failed, setFailed] = useState(0);
  const url = chain[failed];

  if (url) {
    return (
      <img
        src={url}
        // Decorative: the track title is right beside it. An empty alt is the
        // correct value, not a missing one — a missing alt makes a screen reader
        // read the filename.
        alt=""
        onError={() => setFailed((n) => n + 1)}
        className="h-full w-full object-cover"
        data-testid="passport-track-art"
      />
    );
  }

  return (
    <div
      className={`flex h-full w-full items-center justify-center ${MONOGRAM_WASH[status]}`}
      aria-hidden
      data-testid="passport-track-monogram"
    >
      {/* Two characters, not one — the repo's existing `trackMonogram`, reused
          rather than re-derived. Design's mockup shows a single character; two is
          what this codebase already ships and it serves the mockup's OWN stated
          goal better ("a grid of distinct letters, not identical grey squares"),
          because "Busser" and "Barista" collapse to the same square at one
          character. Recorded as a deliberate deviation in the review note. */}
      <span className="font-display text-4xl font-semibold">{trackMonogram(track.title)}</span>
    </div>
  );
};

export default PassportTrackArt;
